import {useIsFeatureStateEnabled} from '../../hooks/use-feature-state'
import {memo, useEffect, useRef} from 'react'
import {MergeBoxPanelContent} from '@github-ui/mergebox'
import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import {useMergeStatusButtonData} from '../../hooks/use-merge-status-button-data'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {getPanelHeading} from './panel-registry'

/**
 * Renders the merge status content inside the centralized docked side panel.
 *
 * Self-contained: sources its data via `useHeaderPageData()` +
 * `useMergeStatusButtonData(...)`. Rendered by `DockedSidePanelContent` when
 * `DockedPanel.MergeStatus` is open. In compact mode, returns `CompactPanel`
 * slots picked up by `CompactSidePanelDialog`.
 *
 * The non-docked Dialog experience for routes without `dockedPanelSupported`
 * continues to be served by `MergeabilitySidesheet` inside
 * `MergeStatusButtonWrapper`.
 */
export const MergeStatusPanel = memo(function MergeStatusPanelComponent() {
  const isOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const updatedPullRequestHeader = useIsFeatureStateEnabled('overview')

  const {
    data: {pullRequest, mergeStatusButtonData},
  } = useHeaderPageData()
  const {helpUrl} = useLayoutRouteData()
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)

  // Auto-close the panel when validated merge data goes away (e.g. after the
  // PR is merged or closed). Mirrors the existing safety net in the headers.
  useEffect(() => {
    if (isOpen && !validatedMergeData) {
      closePanel()
    }
  }, [isOpen, validatedMergeData, closePanel])

  if (!isOpen || !validatedMergeData) return null

  const {viewerLogin, channels} = validatedMergeData
  const content = <MergeBoxPanelContent helpUrl={helpUrl} viewerLogin={viewerLogin} channels={channels} />

  if (!isCompact) {
    return (
      <Panel panelName={DockedPanel.MergeStatus}>
        <Panel.Body className="p-0">{content}</Panel.Body>
      </Panel>
    )
  }

  if (!updatedPullRequestHeader) {
    return (
      <Dialog
        initialFocusRef={closeButtonRef}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
              <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.MergeStatus)}</Dialog.Title>
              <IconButton
                ref={closeButtonRef}
                aria-label="Close merge status panel"
                icon={XIcon}
                variant="invisible"
                onClick={closePanel}
              />
            </div>
          </Dialog.Header>
        )}
      >
        {content}
      </Dialog>
    )
  }

  return <CompactPanel.Content className="p-0">{content}</CompactPanel.Content>
})
